import useCheckoutState from '../hooks/useCheckoutState';
import getInputColorScheme from '../lib/getInputColorScheme';
import getSafeReadableColor from '../lib/getSafeReadableColor';
import darken from '../utils/darken';
import { buildFontFamilyWithFallbacks } from '../lib/fontUtils';
import { THEME_PROPERTY_REGISTRY } from '../theming/themePropertyRegistry';
import getPriceBillingConfig from '../lib/getPriceBillingConfig';
import React from 'react';

// Placeholder colours that meet WCAG AA (4.5:1) on the input background:
// #c0b6ba (1.97 on white) and #737373 (3.19 on #262626) both failed.
//
// Native inputs read this through the --cp_input-placeholder-color variable
// below. Stripe Elements can't: they render in a Stripe-hosted iframe our CSS
// variables don't reach, so PaymentMethods.js and StripeElementsProvider.js
// import these values and pass them to Stripe's appearance API as literals.
// The static fallbacks in theming/checkout.css mirror them by hand.
export const INPUT_PLACEHOLDER_COLOR = {
  light: '#757575',
  dark: '#999999',
};

export const getLayoutVariables = (
  layout,
  elements,
  isDarkMode = false,
  layoutType = 'center',
  tokens = {},
) => {
  const variables = [];
  const colorMode = isDarkMode ? 'dark' : 'light';

  // Token defaults
  const defaultTokenBackground = isDarkMode ? '#141414' : 'var(--color-neutralgray)';
  const defaultTokenSurface = isDarkMode ? '#1c1c1c' : 'var(--cp_surface)';
  const defaultTokenSurfaceAccent = isDarkMode ? '#1c1c1c' : 'var(--color-white)';

  // Resolve token values (user-set or default)
  const tokenBackground = tokens.colorBackground?.[colorMode] || defaultTokenBackground;
  const tokenSurface = tokens.colorSurface?.[colorMode] || defaultTokenSurface;
  const tokenSurfaceAccent = tokens.colorSurfaceAccent?.[colorMode] || defaultTokenSurfaceAccent;

  // Default token→layout mappings per layout type
  // Key difference: double maps content→surfaceAccent (branded panel), others map content→surface
  const tokenMappings = {
    center: { background: tokenBackground, content: tokenSurface, form: tokenSurface },
    double: { background: tokenBackground, content: tokenSurfaceAccent, form: tokenSurface },
    split: { background: tokenBackground, content: tokenSurface, form: tokenSurface },
  };
  const mapping = tokenMappings[layoutType] || tokenMappings.center;

  // Per-layout config key
  const layoutKey =
    layoutType === 'center'
      ? 'centerLayout'
      : layoutType === 'double'
        ? 'doubleLayout'
        : 'splitLayout';
  const perLayout = layout[layoutKey] || {};

  // Resolution: per-layout override → token mapping → CSS default
  const bgColor = perLayout.backgroundColor?.[colorMode] || mapping.background;
  const contentBgColor = perLayout.contentBackgroundColor?.[colorMode] || mapping.content;
  const formBgColor = perLayout.formBackgroundColor?.[colorMode] || mapping.form;

  variables.push(`--cp_background-color: ${bgColor};`);
  variables.push(`--cp_content-background-color: ${contentBgColor};`);
  variables.push(`--cp_form-background-color: ${formBgColor};`);

  // Black/white for the "Powered by Checkout Page" badge, picked from the
  // luminance of the surface it actually sits on (not the light/dark mode flag)
  // so it stays readable when a seller's custom background disagrees with the
  // mode. The badge sits on the page background in the center layout, but on the
  // form column in the double/split/embedded layouts — so emit a color for each
  // and let those columns override --cp_branding-badge-color via the cascade.
  // Non-hex CSS-var defaults (light surfaces) fall back to black.
  variables.push(`--cp_branding-badge-color: ${getSafeReadableColor(bgColor)};`);
  variables.push(`--cp_branding-badge-color-on-form: ${getSafeReadableColor(formBgColor)};`);

  // Emit token CSS variables for use in custom CSS
  variables.push(`--cp_token-background: ${tokenBackground};`);
  variables.push(`--cp_token-surface: ${tokenSurface};`);
  variables.push(`--cp_token-surface-accent: ${tokenSurfaceAccent};`);

  // Background image & position — per-layout
  const bgPosition = perLayout.backgroundPosition;
  if (bgPosition) {
    variables.push(`--cp_background-position: ${bgPosition};`);
  }

  // Input defaults — must be emitted to override CSS dark media query
  // Dark values use hardcoded neutral hex (not var(--color-gray-*)) because
  // the store's Tailwind config has chocolate-tinted grays
  variables.push(`--cp_input-background: ${isDarkMode ? '#262626' : 'var(--color-white)'};`);
  variables.push(`--cp_input-border-color: ${isDarkMode ? '#525252' : 'var(--color-gray-100)'};`);
  variables.push(
    `--cp_input-focus-border-color: ${isDarkMode ? '#737373' : 'var(--color-gray-200)'};`,
  );
  variables.push(
    `--cp_input-placeholder-color: ${isDarkMode ? INPUT_PLACEHOLDER_COLOR.dark : INPUT_PLACEHOLDER_COLOR.light};`,
  );

  // Shadow color and width (global fallback)
  if (layout.shadowColor?.[colorMode]) {
    variables.push(`--cp_checkout-shadow-color: ${layout.shadowColor[colorMode]};`);
  } else {
    variables.push(`--cp_checkout-shadow-color: ${isDarkMode ? '#111111' : '#d4d4d4'};`);
  }

  if (layout.shadowWidth) {
    const shadowValue = getShadowStyle(layout.shadowWidth, 'var(--cp_checkout-shadow-color)');
    variables.push(`--cp_checkout-shadow: ${shadowValue};`);

    const formBackgroundShadowRight = getSideShadowStyle(
      layout.shadowWidth,
      'var(--cp_checkout-shadow-color)',
      'right',
    );
    const formBackgroundShadowLeft = getSideShadowStyle(
      layout.shadowWidth,
      'var(--cp_checkout-shadow-color)',
      'left',
    );
    variables.push(`--cp_checkout-form-background-shadow-right: ${formBackgroundShadowRight};`);
    variables.push(`--cp_checkout-form-background-shadow-left: ${formBackgroundShadowLeft};`);
  }

  variables.push(`--cp_product-text-align: ${elements?.product?.styles?.textAlign || 'left'};`);

  return variables;
};

const getShadowStyle = (style, shadowColor) => {
  const shadowStyleMap = {
    auto: `0 1px 3px 0 ${shadowColor}, 0 1px 2px -1px ${shadowColor}`,
    none: 'none',
    sm: `0 1px 2px 0 ${shadowColor}`,
    md: `0 4px 6px -1px ${shadowColor}, 0 2px 4px -2px ${shadowColor}`,
    lg: `0 10px 15px -3px ${shadowColor}, 0 4px 6px -2px ${shadowColor}`,
    xl: `0 20px 25px -5px ${shadowColor}, 0 8px 10px -6px ${shadowColor}`,
    '2xl': `0 25px 50px -12px ${shadowColor}`,
  };

  return shadowStyleMap[style] || shadowStyleMap.auto;
};

// Side shadow for double layout form background (horizontal instead of vertical)
const getSideShadowStyle = (style, shadowColor, direction = 'right') => {
  const sign = direction === 'left' ? '-' : '';
  const shadowStyleMap = {
    auto: `${sign}2px 0 6px -1px ${shadowColor}`,
    none: 'none',
    sm: `${sign}1px 0 3px 0 ${shadowColor}`,
    md: `${sign}3px 0 8px -2px ${shadowColor}`,
    lg: `${sign}4px 0 12px -2px ${shadowColor}`,
    xl: `${sign}6px 0 16px -3px ${shadowColor}`,
  };

  return shadowStyleMap[style] || shadowStyleMap.auto;
};

const getTokenColorVariables = (
  tokens,
  isDarkMode = false,
  colorPrimary = null,
  colorSecondary = null,
) => {
  const variables = [];
  const colorMode = isDarkMode ? 'dark' : 'light';

  if (tokens.colorPrimary?.[colorMode]) {
    variables.push(`--cp_color-primary: ${tokens.colorPrimary[colorMode]};`);
    variables.push(`--cp_color-primary-dark: ${darken(tokens.colorPrimary[colorMode], 0.1)};`);

    const safeLogoTextColor = getSafeReadableColor(tokens.colorPrimary[colorMode]);
    variables.push(`--cp_store-logo-text-color: ${safeLogoTextColor};`);
    variables.push(`--cp_store-logo-background-color: var(--cp_color-primary);`);
  } else if (colorPrimary) {
    variables.push(`--cp_color-primary: ${colorPrimary};`);
    variables.push(`--cp_color-primary-dark: ${darken(colorPrimary, 0.1)};`);

    const safeLogoTextColor = getSafeReadableColor(colorPrimary);
    variables.push(`--cp_store-logo-text-color: ${safeLogoTextColor};`);
    variables.push(`--cp_store-logo-background-color: var(--cp_color-primary);`);
  }

  const effectiveSecondary = tokens.colorSecondary?.[colorMode] || colorSecondary;
  if (effectiveSecondary) {
    variables.push(`--cp_color-secondary: ${effectiveSecondary};`);
    variables.push(`--cp_color-secondary-dark: ${darken(effectiveSecondary, 0.1)};`);
    // Highlight badge text is contrast-derived against its effective background in
    // getElementStyleVariables (like the button text), not here.
  }

  if (tokens.textPrimary?.[colorMode]) {
    variables.push(`--cp_text-primary: ${tokens.textPrimary[colorMode]};`);
  } else {
    variables.push(`--cp_text-primary: ${isDarkMode ? '#f5f5f5' : 'var(--color-black)'};`);
  }

  if (tokens.textSecondary?.[colorMode]) {
    variables.push(`--cp_text-secondary: ${tokens.textSecondary[colorMode]};`);
  } else {
    variables.push(`--cp_text-secondary: ${isDarkMode ? '#d4d4d4' : 'var(--color-gray-600)'};`);
  }

  if (tokens.textTertiary?.[colorMode]) {
    variables.push(`--cp_text-tertiary: ${tokens.textTertiary[colorMode]};`);
  } else {
    // Light default gray-400 (#a1a1a1) only hit 2.58:1 on white, failing AA for
    // the "Or pay with card" / secure-payments footer text — darken to #757575.
    variables.push(`--cp_text-tertiary: ${isDarkMode ? '#a3a3a3' : '#757575'};`);
  }

  // Semantic surface/border variables — must be emitted by JS to override
  // the @media (prefers-color-scheme: dark) block in checkout.css
  variables.push(`--cp_surface: ${isDarkMode ? '#262626' : 'var(--color-white)'};`);
  variables.push(`--cp_surface-selected: ${isDarkMode ? '#404040' : 'var(--color-white)'};`);
  variables.push(`--cp_border-subtle: ${isDarkMode ? '#404040' : 'var(--color-gray-100)'};`);
  // Price grouping tabs track: needs contrast against BOTH the content
  // surface and the active tab in each mode (light: gray-100 on white card,
  // active tab white; dark: surface step #262626 between content #1c1c1c and
  // active tab #404040). No single existing semantic fits both, so it gets
  // its own theme-aware emission like the surface/border vars above.
  variables.push(
    `--cp_tabs-track-background: ${isDarkMode ? '#262626' : 'var(--color-gray-100, #f0eeee)'};`,
  );
  variables.push(`--cp_border-default: ${isDarkMode ? '#525252' : 'var(--color-gray-200)'};`);
  variables.push(`--cp_text-strong: ${isDarkMode ? '#f5f5f5' : 'var(--color-gray-900)'};`);

  return variables;
};

const calculateLineHeightFromFontSize = fontSizePx => {
  const rem = fontSizePx / 16;

  let ratio = 1.5;
  if (rem <= 1) {
    ratio = 1.5;
  } else if (rem <= 1.5) {
    ratio = 1.5 - (rem - 1) * (0.15 / 0.5);
  } else if (rem <= 3) {
    ratio = 1.35 - (rem - 1.5) * (0.23 / 1.5);
  } else if (rem <= 6) {
    ratio = 1.12 - (rem - 3) * (0.04 / 3);
  } else {
    ratio = 1.06;
  }

  const numerator = +(ratio * rem).toFixed(4);
  return `calc(${numerator} / ${rem})`;
};

const resolveNestedValue = (elements, path) => {
  const elementPath = path.replace(/^variables\.elements\./, '');
  const parts = elementPath.split('.');
  let current = elements;
  for (const part of parts) {
    if (current == null) return undefined;
    current = current[part];
  }
  return current;
};

export const getElementStyleVariables = (
  elements,
  isLightMode = true,
  product = null,
  colorPrimary,
  colorSecondary,
) => {
  const variables = [];
  const colorMode = isLightMode ? 'light' : 'dark';

  // ========================================================================
  // PRE-LOOP DEFAULTS (overridden by explicit theme values from the loop)
  // ========================================================================

  // Trial products without explicit price sizing get body-size to de-emphasize
  // the price. CSS is generated once per page (no live selection here), so a
  // multi-price product is judged by its first enabled price.
  // TODO(M14): legacy product-scalar fallback — remove once the prices[] backfill migration has run (product.trialPeriodDays)
  const firstEnabledPrice = (product?.prices || []).find(p => p.enabled !== false);
  const effectiveTrialPeriodDays =
    product?.prices?.length > 0
      ? getPriceBillingConfig(firstEnabledPrice)?.trialPeriodDays
      : product?.trialPeriodDays;
  if (effectiveTrialPeriodDays && !elements.product?.pricing?.price?.styles?.fontSize) {
    variables.push(`--cp_product-price-font-size: var(--cp_font-body-size);`);
    variables.push(`--cp_product-price-line-height: var(--cp_font-body-line-height);`);
    variables.push(`--cp_product-price-discountedFromPrice-font-size: var(--cp_font-body-size);`);
    variables.push(`--cp_product-price-color: var(--cp_text-secondary);`);
  }

  // Button defaults — the registry loop may override these with explicit theme values
  const buttonBg = elements.checkout?.form?.button?.styles?.backgroundColor?.[colorMode];
  const buttonBgHover = elements.checkout?.form?.button?.hoverStyles?.backgroundColor?.[colorMode];

  variables.push('--cp_submit-button-background-color: var(--cp_color-primary);');
  variables.push('--cp_submit-button-background-color-hover: var(--cp_color-primary-dark);');
  variables.push('--cp_submit-button-border-color: var(--cp_color-primary);');
  variables.push('--cp_submit-button-border-color-hover: var(--cp_color-primary-dark);');

  const safeButtonTextColor = getSafeReadableColor(buttonBg || colorPrimary);
  const hoverBgFallback = colorPrimary ? darken(colorPrimary, 0.1) : colorPrimary;
  const safeButtonTextColorHover = getSafeReadableColor(buttonBgHover || hoverBgFallback);
  variables.push(`--cp_submit-button-text-color: ${safeButtonTextColor};`);
  variables.push(`--cp_submit-button-text-color-hover: ${safeButtonTextColorHover};`);

  // Highlight badge text — like the button, derive a readable colour against the
  // badge's EFFECTIVE background (explicit badge bg, else the secondary accent) so
  // a light secondary no longer yields white-on-light. Emitted before the registry
  // loop, so an explicit seller-set Highlight text colour still wins. Skipped when
  // no concrete colour is resolvable (registry white fallback applies).
  const singleBadgeBg =
    elements.product?.pricing?.badge?.styles?.backgroundColor?.[colorMode] || colorSecondary;
  if (singleBadgeBg) {
    variables.push(`--cp_product-price-badge-color: ${getSafeReadableColor(singleBadgeBg)};`);
  }
  // The Cards highlight is the base the price option pill chains to (and the
  // order bump pill through it), so its text derives against ITS effective
  // background. The option pill gets a text of its own only when the seller
  // gave it a background of its own; otherwise it rests on the Cards text.
  const cardBadgeBg =
    elements.checkout?.cards?.badge?.styles?.backgroundColor?.[colorMode] || colorSecondary;
  if (cardBadgeBg) {
    variables.push(`--cp_card-badge-color: ${getSafeReadableColor(cardBadgeBg)};`);
  }
  const optionBadgeBg =
    elements.product?.pricePicker?.option?.badge?.styles?.backgroundColor?.[colorMode];
  if (optionBadgeBg) {
    variables.push(`--cp_price-picker-option-badge-color: ${getSafeReadableColor(optionBadgeBg)};`);
  }

  // The order bump accept button wears the submit button's colours until the
  // seller paints it, and then its text is the submit button's too (checkout.css
  // reads --cp_submit-button-text-color). Only a background of this button's own
  // needs a text derived against it: black or white by luminance, as the submit
  // button's is. It goes out under a readable-* name of its own rather than the
  // button's text variable, because an outline button reads its text from its
  // border instead and must not pick this up — checkout.css chooses between
  // them by fill. The selected state derives from the selected background when
  // the seller set one; otherwise an explicit resting text colour carries over
  // to the selected button, and only an untouched one is derived.
  const acceptButton = elements.checkout?.form?.orderBumps?.acceptButton;
  const acceptButtonBg = acceptButton?.styles?.backgroundColor?.[colorMode];
  if (acceptButtonBg) {
    variables.push(
      `--cp_checkout-form-orderBumps-acceptButton-readable-color: ${getSafeReadableColor(acceptButtonBg)};`,
    );
  }
  const acceptButtonSelectedBg = acceptButton?.selectedStyles?.backgroundColor?.[colorMode];
  const acceptButtonText = acceptButton?.styles?.color?.[colorMode];
  const acceptButtonSelectedSurface =
    acceptButtonSelectedBg || (acceptButtonText ? null : acceptButtonBg);
  if (acceptButtonSelectedSurface) {
    variables.push(
      `--cp_checkout-form-orderBumps-acceptButton-readable-color-selected: ${getSafeReadableColor(acceptButtonSelectedSurface)};`,
    );
  }

  // Fallback — overridden below if an explicit priceBreakdown color is set
  variables.push(`--cp_price-breakdown-color: var(--color-gray-600);`);

  // ========================================================================
  // DATA-DRIVEN LOOP (iterate registry for element style mappings)
  // ========================================================================

  for (const prop of THEME_PROPERTY_REGISTRY) {
    if (prop.derived || prop.cssVars.length === 0) continue;
    if (!prop.path.startsWith('variables.elements.')) continue;

    const rawValue = resolveNestedValue(elements, prop.path);
    if (rawValue == null) continue;

    // Resolve color mode for dual color objects
    const value =
      typeof rawValue === 'object' && rawValue[colorMode] ? rawValue[colorMode] : rawValue;

    // Skip non-string/number values (objects without color mode key)
    if (typeof value !== 'string' && typeof value !== 'number') continue;

    // Skip falsy scalar values (0, '') to match addStyleVariable behavior
    if (!value) continue;

    for (const mapping of prop.cssVars) {
      switch (mapping.transform) {
        case 'direct':
          variables.push(`--cp_${mapping.cssVar}: ${value};`);
          break;
        case 'px':
          variables.push(`--cp_${mapping.cssVar}: ${value}px;`);
          break;
        case 'lineHeight':
          variables.push(`--cp_${mapping.cssVar}: ${calculateLineHeightFromFontSize(value)};`);
          break;
        case 'shadow': {
          const shadowColorVar = mapping.cssVar.replace('box-shadow', 'box-shadow-color');
          variables.push(
            `--cp_${mapping.cssVar}: ${getShadowStyle(value, `var(--cp_${shadowColorVar}, rgb(0 0 0 / 15%))`)};`,
          );
          break;
        }
      }
    }
  }

  // ========================================================================
  // POST-LOOP SPECIAL CASES (conditional logic beyond simple mapping)
  // ========================================================================

  // The main loop skips falsy values, so an explicit 0 on the price picker
  // (border width "None", radius/padding/gap 0) could never override the
  // non-zero generated defaults. Force those zeros through. Order bumps rest
  // on the picker's variables in checkout.css, and the picker's pill on the
  // Cards highlight, so their zeros need the same.
  for (const prop of THEME_PROPERTY_REGISTRY) {
    if (
      !prop.path.startsWith('variables.elements.product.pricePicker') &&
      !prop.path.startsWith('variables.elements.checkout.form.orderBumps') &&
      !prop.path.startsWith('variables.elements.checkout.cards.badge')
    )
      continue;
    const rawValue = resolveNestedValue(elements, prop.path);
    if (rawValue === '' || rawValue == null || Number(rawValue) !== 0) continue;
    for (const mapping of prop.cssVars) {
      if (mapping.transform === 'px') {
        variables.push(`--cp_${mapping.cssVar}: 0px;`);
      }
    }
  }

  // borderWidth: 0 needs transparent color to prevent the 1px default border from showing
  const checkoutBorderWidth = Number(elements.checkout?.styles?.borderWidth);
  if (!isNaN(checkoutBorderWidth) && checkoutBorderWidth === 0) {
    variables.push(`--cp_checkout-border-color: transparent;`);
    variables.push(`--cp_checkout-border-width: 0px;`);
  }

  const variantBorderWidth = Number(
    elements.checkout?.variants?.variantOptions?.styles?.borderWidth,
  );
  if (!isNaN(variantBorderWidth) && variantBorderWidth === 0) {
    variables.push(`--cp_checkout-variants-variantOptions-border-color: transparent;`);
    variables.push(`--cp_checkout-variants-variantOptions-border-width: 0px;`);
  }

  const ticketBorderWidth = Number(
    elements.checkout?.ticketGroups?.ticketTypes?.styles?.borderWidth,
  );
  if (!isNaN(ticketBorderWidth) && ticketBorderWidth === 0) {
    variables.push(`--cp_checkout-ticketGroups-ticketTypes-border-color: transparent;`);
    variables.push(`--cp_checkout-ticketGroups-ticketTypes-border-width: 0px;`);
  }

  // collapsibles.border.enabled=false zeroes out the border so user-chosen width/color
  // can be preserved in the theme but visually hidden.
  if (elements.description?.collapsibles?.border?.enabled === false) {
    variables.push(`--cp_description-collapsibles-border-width: 0px;`);
    variables.push(`--cp_description-collapsibles-border-color: transparent;`);
  }

  const inputBackgroundColor = elements.checkout?.form?.fields?.input?.styles?.backgroundColor;
  const explicitInputBackground =
    typeof inputBackgroundColor === 'object' && inputBackgroundColor !== null
      ? inputBackgroundColor[colorMode]
      : inputBackgroundColor;
  variables.push(
    `--cp_checkout-form-fields-input-color-scheme: ${getInputColorScheme(explicitInputBackground, colorMode)};`,
  );

  // Only emit focus overrides when they differ from the base input styles
  if (elements.checkout?.form?.fields?.input?.focusStyles) {
    const focusStyles = elements.checkout.form.fields.input.focusStyles;
    const inputStyles = elements.checkout.form.fields.input.styles;

    const focusBg = focusStyles.backgroundColor?.[colorMode];
    const inputBg = inputStyles?.backgroundColor?.[colorMode];
    if (focusBg && focusBg !== inputBg) {
      variables.push(`--cp_checkout-form-fields-input-focus-background-color: ${focusBg};`);
    }

    const focusColor = focusStyles.color?.[colorMode];
    const inputColor = inputStyles?.color?.[colorMode];
    if (focusColor && focusColor !== inputColor) {
      variables.push(`--cp_checkout-form-fields-input-focus-color: ${focusColor};`);
    }

    const focusBorderColor = focusStyles.borderColor?.[colorMode];
    const inputBorderColor = inputStyles?.borderColor?.[colorMode];
    if (focusBorderColor && focusBorderColor !== inputBorderColor) {
      variables.push(`--cp_checkout-form-fields-input-focus-border-color: ${focusBorderColor};`);
    }
  }

  // Hidden placeholders use transparent color instead of display:none to preserve layout
  const placeholder = elements.checkout?.form?.fields?.input?.placeholder;
  const placeholderStyles = elements.checkout?.form?.fields?.input?.placeholderStyles;
  const isPlaceholderEnabled =
    typeof placeholder?.enabled === 'boolean'
      ? placeholder.enabled
      : typeof placeholderStyles?.enabled === 'boolean'
        ? placeholderStyles.enabled
        : true;

  if (!isPlaceholderEnabled) {
    variables.push(`--cp_checkout-form-fields-input-placeholder-color: transparent;`);
  } else if (placeholderStyles?.color?.[colorMode]) {
    variables.push(
      `--cp_checkout-form-fields-input-placeholder-color: ${placeholderStyles.color[colorMode]};`,
    );
  }

  // Explicit theme value overrides the gray-600 fallback set above
  if (elements.checkout?.form?.priceBreakdown?.styles?.color?.[colorMode]) {
    variables.push(
      `--cp_price-breakdown-color: ${elements.checkout.form.priceBreakdown.styles.color[colorMode]};`,
    );
  }

  // Map text alignment values to flexbox equivalents for the price breakdown layout
  const alignment = elements.checkout?.form?.priceBreakdown?.styles?.alignment;
  if (alignment) {
    const alignmentMap = {
      left: { justify: 'start', items: 'start' },
      center: { justify: 'center', items: 'center' },
      right: { justify: 'end', items: 'end' },
      justify: { justify: 'space-between', items: 'stretch' },
    };
    const flexValues = alignmentMap[alignment] || alignmentMap.right;
    variables.push(`--cp_checkout-form-priceBreakdown-justify: ${flexValues.justify};`);
    variables.push(`--cp_checkout-form-priceBreakdown-items-align: ${flexValues.items};`);
  }

  return variables;
};

export const getSpacing = tokens => {
  const spacingSizeMap = {
    sm: '0.5rem',
    md: '1rem',
    lg: '1.5rem',
  };

  return spacingSizeMap[tokens.spacing] || spacingSizeMap.md;
};

export const getRadius = tokens => {
  const radiusSizeMap = {
    none: {
      checkout: '0px',
      card: '0px',
      input: '0px',
      button: '0px',
    },
    sm: {
      checkout: '8px',
      card: '8px',
      input: '4px',
      button: '4px',
    },
    md: {
      checkout: '16px',
      card: '16px',
      input: '8px',
      button: '8px',
    },
    lg: {
      checkout: '24px',
      card: '24px',
      input: '12px',
      button: '12px',
    },
    xl: {
      checkout: '32px',
      card: '32px',
      input: '16px',
      button: '16px',
    },
    full: {
      checkout: '48px',
      card: '64px',
      input: '9999px',
      button: '9999px',
    },
  };

  return radiusSizeMap[tokens.radius] || radiusSizeMap.sm;
};

export const getFontHeadingSizes = tokens => {
  const sizeMap = {
    xs: {
      h1: { fontSize: '1.25rem', lineHeight: calculateLineHeightFromFontSize(1.25 * 16) },
      h2: { fontSize: '1.125rem', lineHeight: calculateLineHeightFromFontSize(1.125 * 16) },
      h3: { fontSize: '1rem', lineHeight: calculateLineHeightFromFontSize(1 * 16) },
      h4: { fontSize: '0.875rem', lineHeight: calculateLineHeightFromFontSize(0.875 * 16) },
      h5: { fontSize: '0.75rem', lineHeight: calculateLineHeightFromFontSize(0.75 * 16) },
      h6: { fontSize: '0.625rem', lineHeight: calculateLineHeightFromFontSize(0.625 * 16) },
    },
    sm: {
      h1: { fontSize: '1.5rem', lineHeight: calculateLineHeightFromFontSize(1.5 * 16) },
      h2: { fontSize: '1.25rem', lineHeight: calculateLineHeightFromFontSize(1.25 * 16) },
      h3: { fontSize: '1.125rem', lineHeight: calculateLineHeightFromFontSize(1.125 * 16) },
      h4: { fontSize: '1rem', lineHeight: calculateLineHeightFromFontSize(1 * 16) },
      h5: { fontSize: '0.875rem', lineHeight: calculateLineHeightFromFontSize(0.875 * 16) },
      h6: { fontSize: '0.75rem', lineHeight: calculateLineHeightFromFontSize(0.75 * 16) },
    },
    md: {
      h1: { fontSize: '1.75rem', lineHeight: calculateLineHeightFromFontSize(1.75 * 16) },
      h2: { fontSize: '1.5rem', lineHeight: calculateLineHeightFromFontSize(1.5 * 16) },
      h3: { fontSize: '1.25rem', lineHeight: calculateLineHeightFromFontSize(1.25 * 16) },
      h4: { fontSize: '1.125rem', lineHeight: calculateLineHeightFromFontSize(1.125 * 16) },
      h5: { fontSize: '1rem', lineHeight: calculateLineHeightFromFontSize(1 * 16) },
      h6: { fontSize: '0.875rem', lineHeight: calculateLineHeightFromFontSize(0.875 * 16) },
    },
    lg: {
      h1: { fontSize: '2rem', lineHeight: calculateLineHeightFromFontSize(2 * 16) },
      h2: { fontSize: '1.75rem', lineHeight: calculateLineHeightFromFontSize(1.75 * 16) },
      h3: { fontSize: '1.5rem', lineHeight: calculateLineHeightFromFontSize(1.5 * 16) },
      h4: { fontSize: '1.25rem', lineHeight: calculateLineHeightFromFontSize(1.25 * 16) },
      h5: { fontSize: '1.125rem', lineHeight: calculateLineHeightFromFontSize(1.125 * 16) },
      h6: { fontSize: '1rem', lineHeight: calculateLineHeightFromFontSize(1 * 16) },
    },
    xl: {
      h1: { fontSize: '2.5rem', lineHeight: calculateLineHeightFromFontSize(2.5 * 16) },
      h2: { fontSize: '2.25rem', lineHeight: calculateLineHeightFromFontSize(2.25 * 16) },
      h3: { fontSize: '2rem', lineHeight: calculateLineHeightFromFontSize(2 * 16) },
      h4: { fontSize: '1.75rem', lineHeight: calculateLineHeightFromFontSize(1.75 * 16) },
      h5: { fontSize: '1.5rem', lineHeight: calculateLineHeightFromFontSize(1.5 * 16) },
      h6: { fontSize: '1.25rem', lineHeight: calculateLineHeightFromFontSize(1.25 * 16) },
    },
  };

  return sizeMap[tokens.fontHeading?.size] || sizeMap.md;
};

export const getFontBodySizes = tokens => {
  const sizeMap = {
    xs: {
      xs: { fontSize: '0.375rem', lineHeight: calculateLineHeightFromFontSize(0.375 * 16) },
      sm: { fontSize: '0.5rem', lineHeight: calculateLineHeightFromFontSize(0.5 * 16) },
      base: { fontSize: '0.625rem', lineHeight: calculateLineHeightFromFontSize(0.625 * 16) },
      lg: { fontSize: '0.75rem', lineHeight: calculateLineHeightFromFontSize(0.75 * 16) },
    },
    sm: {
      xs: { fontSize: '0.5rem', lineHeight: calculateLineHeightFromFontSize(0.5 * 16) },
      sm: { fontSize: '0.625rem', lineHeight: calculateLineHeightFromFontSize(0.625 * 16) },
      base: { fontSize: '0.75rem', lineHeight: calculateLineHeightFromFontSize(0.75 * 16) },
      lg: { fontSize: '0.875rem', lineHeight: calculateLineHeightFromFontSize(0.875 * 16) },
    },
    md: {
      xs: { fontSize: '0.625rem', lineHeight: calculateLineHeightFromFontSize(0.625 * 16) },
      sm: { fontSize: '0.75rem', lineHeight: calculateLineHeightFromFontSize(0.75 * 16) },
      base: { fontSize: '0.875rem', lineHeight: calculateLineHeightFromFontSize(0.875 * 16) },
      lg: { fontSize: '1rem', lineHeight: calculateLineHeightFromFontSize(1 * 16) },
    },
    lg: {
      xs: { fontSize: '0.75rem', lineHeight: calculateLineHeightFromFontSize(0.75 * 16) },
      sm: { fontSize: '0.875rem', lineHeight: calculateLineHeightFromFontSize(0.875 * 16) },
      base: { fontSize: '1rem', lineHeight: calculateLineHeightFromFontSize(1 * 16) },
      lg: { fontSize: '1.125rem', lineHeight: calculateLineHeightFromFontSize(1.125 * 16) },
    },
    xl: {
      xs: { fontSize: '0.875rem', lineHeight: calculateLineHeightFromFontSize(0.875 * 16) },
      sm: { fontSize: '1rem', lineHeight: calculateLineHeightFromFontSize(1 * 16) },
      base: { fontSize: '1.125rem', lineHeight: calculateLineHeightFromFontSize(1.125 * 16) },
      lg: { fontSize: '1.25rem', lineHeight: calculateLineHeightFromFontSize(1.25 * 16) },
    },
  };

  return sizeMap[tokens.fontBody?.size] || sizeMap.md;
};

export const getFontFamilies = tokens => {
  const mainFont = tokens.fontMain?.family || '';
  const defaultFallback =
    'Inter, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Oxygen-Sans, Ubuntu, Cantarell, "Helvetica Neue", sans-serif';

  const headingFontFamily = tokens.fontHeading?.family || mainFont;
  const headingFont = headingFontFamily
    ? buildFontFamilyWithFallbacks(headingFontFamily)
    : defaultFallback;

  const bodyFontFamily = tokens.fontBody?.family || mainFont;
  const bodyFont = bodyFontFamily ? buildFontFamilyWithFallbacks(bodyFontFamily) : defaultFallback;

  return {
    heading: headingFont,
    body: bodyFont,
  };
};

export const getFontPriceSize = tokens => {
  const sizeMap = {
    xs: { fontSize: '1.125rem', lineHeight: calculateLineHeightFromFontSize(1.125 * 16) },
    sm: { fontSize: '1.25rem', lineHeight: calculateLineHeightFromFontSize(1.25 * 16) },
    md: { fontSize: '1.5rem', lineHeight: calculateLineHeightFromFontSize(1.5 * 16) },
    lg: { fontSize: '1.75rem', lineHeight: calculateLineHeightFromFontSize(1.75 * 16) },
    xl: { fontSize: '2rem', lineHeight: calculateLineHeightFromFontSize(2 * 16) },
  };

  return sizeMap[tokens.fontBody?.size] || sizeMap.md;
};

const getStyleTokens = tokens => {
  const spacing = getSpacing(tokens);
  const radius = getRadius(tokens);
  const fontHeadingSizes = getFontHeadingSizes(tokens);
  const fontHeadingStyle = tokens.fontHeading?.style || 'normal';
  const fontBodySizes = getFontBodySizes(tokens);
  const fontBodyStyle = tokens.fontBody?.style || 'normal';
  const fontPriceSize = getFontPriceSize(tokens);
  const { heading: headingFont, body: bodyFont } = getFontFamilies(tokens);

  return [
    `--cp_checkout-border-radius: ${radius.checkout || '4px'};`,
    `--cp_card-border-radius: ${radius.card || '4px'};`,
    `--cp_input-border-radius: ${radius.input || '4px'};`,
    `--cp_button-border-radius: ${radius.button || '4px'};`,

    `--cp_font-heading: ${headingFont};`,
    `--cp_font-heading-style: ${fontHeadingStyle};`,
    `--cp_font-heading-size-h1: ${fontHeadingSizes.h1.fontSize};`,
    `--cp_font-heading-size-h2: ${fontHeadingSizes.h2.fontSize};`,
    `--cp_font-heading-size-h3: ${fontHeadingSizes.h3.fontSize};`,
    `--cp_font-heading-size-h4: ${fontHeadingSizes.h4.fontSize};`,
    `--cp_font-heading-size-h5: ${fontHeadingSizes.h5.fontSize};`,
    `--cp_font-heading-size-h6: ${fontHeadingSizes.h6.fontSize};`,
    `--cp_font-heading-line-height-h1: ${fontHeadingSizes.h1.lineHeight};`,
    `--cp_font-heading-line-height-h2: ${fontHeadingSizes.h2.lineHeight};`,
    `--cp_font-heading-line-height-h3: ${fontHeadingSizes.h3.lineHeight};`,
    `--cp_font-heading-line-height-h4: ${fontHeadingSizes.h4.lineHeight};`,
    `--cp_font-heading-line-height-h5: ${fontHeadingSizes.h5.lineHeight};`,
    `--cp_font-heading-line-height-h6: ${fontHeadingSizes.h6.lineHeight};`,
    `--cp_font-heading-weight: ${tokens.fontHeading?.weight || 700};`,
    `--cp_font-heading-weight-light: ${tokens.fontHeading?.weight ? tokens.fontHeading.weight - 100 : 600};`,

    `--cp_font-body: ${bodyFont};`,
    `--cp_font-body-style: ${fontBodyStyle};`,
    `--cp_font-body-size-xs: ${fontBodySizes.xs.fontSize};`,
    `--cp_font-body-size: ${fontBodySizes.base.fontSize};`,
    `--cp_font-body-size-sm: ${fontBodySizes.sm.fontSize};`,
    `--cp_font-body-size-lg: ${fontBodySizes.lg.fontSize};`,

    `--cp_font-body-line-height-xs: ${fontBodySizes.xs.lineHeight};`,
    `--cp_font-body-line-height: ${fontBodySizes.base.lineHeight};`,
    `--cp_font-body-line-height-sm: ${fontBodySizes.sm.lineHeight};`,
    `--cp_font-body-line-height-lg: ${fontBodySizes.lg.lineHeight};`,

    `--cp_font-price-size: ${fontPriceSize.fontSize};`,
    `--cp_font-price-line-height: ${fontPriceSize.lineHeight};`,

    `--cp_font-body-weight: ${tokens.fontBody?.weight || 400};`,
    `--cp_font-label-weight: ${tokens.fontBody?.weight ? tokens.fontBody.weight + 100 : 500};`,

    `--cp_spacing: ${spacing};`,
  ];
};

/**
 * Determines whether the preview should render in dark mode.
 *
 * When the theme color scheme is explicitly 'light' or 'dark', the preview
 * toggle is ignored and the theme setting wins.  Only when the scheme is
 * 'system' (or unset) does the preview toggle take effect.
 */
export function getEffectivePreviewDark(colorScheme, previewColorMode) {
  if (colorScheme === 'dark') return true;
  if (colorScheme === 'light') return false;
  // 'system' is the only mode where the preview toggle takes effect.
  // undefined/null means the default ('light') was stripped by
  // stripSystemDefaults — treat the same as explicit 'light'.
  if (colorScheme === 'system') return previewColorMode === 'dark';
  return false;
}

// Default form input styling to ensure all inputs have proper appearance,
// layered so component utilities and seller CSS deterministically
// override it (layer order declared in each consumer's Tailwind entry
// CSS: theme, base, cp-base, components, utilities).
export const DEFAULT_INPUT_STYLES = `
      @layer cp-base {
      /* iOS zooms the page into any focused field under 16px. */
      @supports (-webkit-touch-callout: none) {
        :root {
          --cp_input-min-font-size: 16px;
        }
      }

      .cp_form-label {
        color: var(--cp_checkout-form-fields-label-color);
        font-family: var(--cp_checkout-form-fields-label-font-family);
        font-style: var(--cp_checkout-form-fields-label-font-style);
        font-size: var(--cp_checkout-form-fields-label-font-size);
        font-weight: var(--cp_checkout-form-fields-label-font-weight);
      }

      .group.focus .cp_form-label {
        color: var(--cp_checkout-form-fields-label-focus-color);
      }

      .cp_form-textinput,
      .cp_form-textarea,
      .cp_form-select,
      .cp_form-telinput,
      .iti__tel-input,
      .cp_form-checkbox,
      .cp_form-radio,
      .cp_stripe-card-element {
        background-color: var(--cp_checkout-form-fields-input-background-color);
        color: var(--cp_checkout-form-fields-input-color);
        border-radius: var(--cp_checkout-form-fields-input-border-radius);
        /* Use a real border like shadcn */
        border: var(--cp_checkout-form-fields-input-border-width) solid var(--cp_checkout-form-fields-input-border-color);
        box-shadow: var(--cp_checkout-form-fields-input-box-shadow, 0 1px 2px 0 rgb(0 0 0 / 5%));
        transition: border-color 0.15s ease-in-out, box-shadow 0.15s ease-in-out;
        font-family: var(--cp_checkout-form-fields-input-font-family);
        font-size: max(var(--cp_input-min-font-size, 0px), var(--cp_checkout-form-fields-input-font-size));
        font-weight: var(--cp_checkout-form-fields-input-font-weight);
        font-style: var(--cp_checkout-form-fields-input-font-style);
        line-height: var(--cp_checkout-form-fields-input-line-height);
      }

      .iti__selected-dial-code {
        color: var(--cp_checkout-form-fields-input-color);
      }

      .cp_form-radio {
        border-radius: 50%;
      }

      .cp_form-checkbox:checked,
      .cp_form-radio:checked {
        background-color: var(--cp_color-secondary);
      }

      .cp_form-textinput,
      .cp_form-textarea,
      .cp_form-select,
      .cp_form-telinput,
      .iti__tel-input,
      .cp_stripe-card-element {
        padding: var(--cp_checkout-form-fields-input-padding-y) var(--cp_checkout-form-fields-input-padding-x);
      }

      .cp_form-textinput:focus,
      .cp_form-textarea:focus,
      .cp_form-select:focus,
      .cp_form-telinput:focus,
      .iti__tel-input:focus,
      .cp_form-checkbox:focus,
      .cp_form-radio:focus,
      .cp_stripe-card-element:focus,
      .cp_stripe-card-element.StripeElement--focus {
        background-color: var(--cp_checkout-form-fields-input-focus-background-color);
        color: var(--cp_checkout-form-fields-input-focus-color);

        /* Change border color on focus */
        border-color: var(--cp_checkout-form-fields-input-focus-border-color);

        /* Add ring effect using box-shadow */
        box-shadow: 
          0 0 0 3px var(--cp_checkout-form-fields-input-focus-ring-color),
          var(--cp_checkout-form-fields-input-box-shadow, 0 1px 2px 0 rgb(0 0 0 / 5%));
      }

      .cp_form-checkbox:checked,
      .cp_form-radio:checked {
        background-color: var(--cp_color-secondary);
      }

      .cp_form-textinput::placeholder,
      .cp_form-textarea::placeholder,
      .cp_form-telinput::placeholder,
      .cp_form-multiselect-placeholder,
      .cp_form-select[data-placeholder='true'] {
        color: var(--cp_checkout-form-fields-input-placeholder-color);
      }

      /* The native date/datetime picker icon is a UA background-image that
         ignores the input's color, so replace it: the background shorthand
         clears the UA artwork, and a calendar-shaped mask (same glyph and
         1em size as Chrome's own icon) repaints it with currentColor */
      .cp_form-textinput::-webkit-calendar-picker-indicator {
        background: currentColor;
        -webkit-mask-image: url("data:image/svg+xml,%3Csvg%20xmlns%3D%22http%3A%2F%2Fwww.w3.org%2F2000%2Fsvg%22%20viewBox%3D%220%200%2024%2024%22%3E%3Cpath%20fill%3D%22black%22%20d%3D%22M20%203h-1V1h-2v2H7V1H5v2H4c-1.1%200-2%20.9-2%202v16c0%201.1.9%202%202%202h16c1.1%200%202-.9%202-2V5c0-1.1-.9-2-2-2zm0%2018H4V8h16v13z%22%2F%3E%3C%2Fsvg%3E");
        -webkit-mask-size: 1em;
        -webkit-mask-repeat: no-repeat;
        -webkit-mask-position: center;
        mask-image: url("data:image/svg+xml,%3Csvg%20xmlns%3D%22http%3A%2F%2Fwww.w3.org%2F2000%2Fsvg%22%20viewBox%3D%220%200%2024%2024%22%3E%3Cpath%20fill%3D%22black%22%20d%3D%22M20%203h-1V1h-2v2H7V1H5v2H4c-1.1%200-2%20.9-2%202v16c0%201.1.9%202%202%202h16c1.1%200%202-.9%202-2V5c0-1.1-.9-2-2-2zm0%2018H4V8h16v13z%22%2F%3E%3C%2Fsvg%3E");
        mask-size: 1em;
        mask-repeat: no-repeat;
        mask-position: center;
      }



      /* Autofilled fields at rest deliberately keep the browser's own
         autofill styling: every engine forces background-color,
         background-image: none and color with UA-!important declarations
         author CSS cannot beat. The UA pairs its autofill background with a
         matching text color, and both resolve against the input's
         color-scheme below, so the highlight is readable on any theme. */
      .cp_form-textinput,
      .cp_form-textarea,
      .cp_form-select,
      .cp_form-telinput,
      .iti__tel-input {
        color-scheme: var(--cp_checkout-form-fields-input-color-scheme, light);
      }

      /* The field the user is actually in follows the theme's focus styles,
         autofilled or not. box-shadow is the one paint channel the UA
         autofill sheet leaves to authors, so the focus background rides in
         as an opaque inset cover under the ring, and the first keystroke —
         which drops the autofilled state — lands on identical colors. */
      .cp_form-textinput:-webkit-autofill:focus,
      .cp_form-textarea:-webkit-autofill:focus,
      .cp_form-select:-webkit-autofill:focus,
      .cp_form-telinput:-webkit-autofill:focus,
      .iti__tel-input:-webkit-autofill:focus {
        -webkit-text-fill-color: var(--cp_checkout-form-fields-input-focus-color) !important;
        caret-color: var(--cp_checkout-form-fields-input-focus-color);
        box-shadow:
          inset 0 0 0 1000px var(--cp_checkout-form-fields-input-focus-background-color),
          0 0 0 3px var(--cp_checkout-form-fields-input-focus-ring-color),
          var(--cp_checkout-form-fields-input-box-shadow, 0 1px 2px 0 rgb(0 0 0 / 5%));
      }

      /* Safari's autofill affordance is a masked glyph the UA sheet paints with
         a plain background-color: black, so it disappears into a dark field.
         Author styles outrank the UA sheet here (no !important on its side),
         which also drops its blue hover state, so the glyph carries its own:
         it sits at reduced alpha and comes up to the full input text color on
         hover. Alpha rather than a lighten/darken because the input color is
         light on dark themes and dark on light ones, so any fixed direction
         would push it into the field background on half the themes. The plain
         color is repeated first as the fallback for a seller custom property
         that color-mix can't parse, which would otherwise leave UA black. */
      .cp_form-textinput::-webkit-contacts-auto-fill-button,
      .cp_form-textinput::-webkit-credit-card-auto-fill-button,
      .cp_form-telinput::-webkit-contacts-auto-fill-button,
      .cp_form-telinput::-webkit-credit-card-auto-fill-button,
      .iti__tel-input::-webkit-contacts-auto-fill-button,
      .iti__tel-input::-webkit-credit-card-auto-fill-button {
        background-color: var(--cp_checkout-form-fields-input-color);
        background-color: color-mix(in srgb, var(--cp_checkout-form-fields-input-color) 70%, transparent);
      }

      .cp_form-textinput::-webkit-contacts-auto-fill-button:hover,
      .cp_form-textinput::-webkit-credit-card-auto-fill-button:hover,
      .cp_form-telinput::-webkit-contacts-auto-fill-button:hover,
      .cp_form-telinput::-webkit-credit-card-auto-fill-button:hover,
      .iti__tel-input::-webkit-contacts-auto-fill-button:hover,
      .iti__tel-input::-webkit-credit-card-auto-fill-button:hover {
        background-color: var(--cp_checkout-form-fields-input-color);
      }

      /* Cap the group's outer radius at ~half a row's height so very large
         input radii (e.g., a fully pill 128px) don't turn the tall multi-row
         container into a giant oval. Rows are flush (!rounded-none) so this
         is the only visible radius. Half-row-height ≈ padding-y + ½ line-height;
         we approximate the ½ line-height as 0.75em (default line-height ~1.5),
         so the cap grows with the theme's vertical padding. */
      .cp-address-inputs-group,
      .cp-address-autocomplete-options {
        border-radius: min(
          var(--cp_checkout-form-fields-input-border-radius),
          calc(var(--cp_checkout-form-fields-input-padding-y) + 0.75em)
        );
      }

      /* Address group reads as a single input: lift the themed box-shadow
         from each row up to the wrapper so we get one cohesive shadow that
         hugs the rounded corners, instead of each row casting its own
         shadow (which bleeds between rows and past the rounded corners).
         Use the themed border-width too so the wrapper outline matches
         standalone inputs (Tailwind border utility would lock it to 1px). */
      .cp-address-inputs-group {
        border-style: solid;
        border-width: var(--cp_checkout-form-fields-input-border-width);
        box-shadow: var(--cp_checkout-form-fields-input-box-shadow, 0 1px 2px 0 rgb(0 0 0 / 5%));
        /* Wrapper-inner-radius = outer radius minus border-width. Used to
           round the focused input's outer corners on first/last rows so
           the focus border + ring follow the wrapper's curve cleanly.
           Clamped at 0 to handle themes where border-width > outer radius. */
        --cp-address-row-corner-radius: max(
          0px,
          calc(
            min(
              var(--cp_checkout-form-fields-input-border-radius),
              calc(var(--cp_checkout-form-fields-input-padding-y) + 0.75em)
            ) - var(--cp_checkout-form-fields-input-border-width)
          )
        );
      }
      /* Inter-row dividers use the same themed width as the outer border, so
         picking a thicker border-width in the theme scales the dividers too. */
      .cp-address-inputs-group > * + * {
        border-top: var(--cp_checkout-form-fields-input-border-width) solid var(--cp_checkout-form-fields-input-border-color);
      }

      /* Address group rows: the bg is set on the bordered container so it
         can be clipped by border-radius. The individual input bgs would
         otherwise extend rectangularly past the rounded corners. Also
         strip the per-row drop shadow (lifted onto the wrapper above).
         No !important: these selectors (0-2-1: class + element + class)
         already out-specificity the base .cp_form-textinput etc. rule
         above (0-1-0) that sets the same properties, so plain declarations
         win here — and correctly still lose to unlayered seller custom CSS. */
      .cp-address-inputs-group input.cp_form-textinput,
      .cp-address-inputs-group select.cp_form-select,
      .cp-address-inputs-group .iti__tel-input,
      .cp-address-inputs-group .cp_form-telinput {
        background-color: transparent;
        box-shadow: none;
      }

      /* Per-row focus: same outset border + ring as standalone inputs.
         Sit on top of the wrapper border (position:relative + z-index) so
         the focused row's outline overlays the wrapper at that section.
         No !important: for the text/select rows and the country-selector
         button, these selectors (0-3-1 with :focus) already out-specificity
         the base :focus rule above (0-2-0) and have no competing cp-base
         declaration, so plain declarations hold — and correctly still lose
         to unlayered seller custom CSS. The phone number input itself
         (.iti__tel-input) is also targeted by the deliberately-unlayered
         phone reset further down (box-shadow/background-color !important),
         which would otherwise beat this layered rule on focus; that reset
         block re-asserts this focus background/shadow with its own
         unlayered !important override right after it, so all four inputs
         end up visually consistent. */
      .cp-address-inputs-group input.cp_form-textinput:focus,
      .cp-address-inputs-group select.cp_form-select:focus,
      .cp-address-inputs-group .iti__tel-input:focus,
      .cp-address-inputs-group .iti__selected-country:focus,
      .cp-address-inputs-group .iti__selected-country:focus-visible {
        background-color: var(--cp_checkout-form-fields-input-focus-background-color);
        outline: none;
        box-shadow:
          0 0 0 var(--cp_checkout-form-fields-input-border-width) var(--cp_checkout-form-fields-input-focus-border-color),
          0 0 0 calc(var(--cp_checkout-form-fields-input-border-width) + 3px) var(--cp_checkout-form-fields-input-focus-ring-color);
        position: relative;
        z-index: 1;
      }

      /* Autofilled variant of the row focus above: the UA overrides the
         background-color, so the focus background rides in as an inset
         cover, like the standalone-input autofill:focus rule. */
      .cp-address-inputs-group input.cp_form-textinput:-webkit-autofill:focus,
      .cp-address-inputs-group select.cp_form-select:-webkit-autofill:focus,
      .cp-address-inputs-group .iti__tel-input:-webkit-autofill:focus {
        box-shadow:
          inset 0 0 0 1000px var(--cp_checkout-form-fields-input-focus-background-color),
          0 0 0 var(--cp_checkout-form-fields-input-border-width) var(--cp_checkout-form-fields-input-focus-border-color),
          0 0 0 calc(var(--cp_checkout-form-fields-input-border-width) + 3px) var(--cp_checkout-form-fields-input-focus-ring-color);
      }
      } /* end @layer cp-base */

      /* Address-row radius overrides stay in @layer utilities (NOT cp-base) —
         they beat Tailwind's .!rounded-none utility (AddressInputs.tsx's
         rowInputClass) via same-layer specificity/order, not via layer
         ordering. First/last row rounded corners are applied at all times,
         not only on focus. The focused row's bg + outset focus shadow
         already fit the wrapper's curve thanks to these radii; the
         unfocused row's bg is transparent so the radius is invisible.
         Applying on focus only would cause the radius to flip 0 <-> inner
         on focus/blur, flashing the rectangular white focus-bg through the
         wrapper curve during the bg transition. */
      @layer utilities {
        .cp-address-row[data-row-position='first'] input.cp_form-textinput,
        .cp-address-row[data-row-position='first'] select.cp_form-select {
          border-top-left-radius: var(--cp-address-row-corner-radius) !important;
          border-top-right-radius: var(--cp-address-row-corner-radius) !important;
        }
        .cp-address-row[data-row-position='last'] input.cp_form-textinput,
        .cp-address-row[data-row-position='last'] select.cp_form-select {
          border-bottom-left-radius: var(--cp-address-row-corner-radius) !important;
          border-bottom-right-radius: var(--cp-address-row-corner-radius) !important;
        }
        .cp-address-phone-row[data-row-position='first'] .iti__selected-country {
          border-top-left-radius: var(--cp-address-row-corner-radius) !important;
        }
        .cp-address-phone-row[data-row-position='first'] .iti__tel-input {
          border-top-right-radius: var(--cp-address-row-corner-radius) !important;
        }
        .cp-address-phone-row[data-row-position='last'] .iti__selected-country {
          border-bottom-left-radius: var(--cp-address-row-corner-radius) !important;
        }
        .cp-address-phone-row[data-row-position='last'] .iti__tel-input {
          border-bottom-right-radius: var(--cp-address-row-corner-radius) !important;
        }
      }

      /* Phone-widget (iti) rules stay UNLAYERED deliberately: their
         border-radius: 0 !important must keep LOSING to the @layer utilities
         address-radius block above (unlayered !important is the weakest
         important tier), so merged address rows keep their outer corner
         rounding. Do not move these into cp-base. */
      /* Phone input embedded inside the address field group: strip the
         standalone telinput chrome so the iti widget visually merges into
         the bordered, divided address rows container. */
      .cp-address-phone-row .iti {
        width: 100%;
        display: flex;
      }
      .cp-address-phone-row .iti__tel-input,
      .cp-address-phone-row .cp_form-telinput {
        border: 0 !important;
        border-radius: 0 !important;
        box-shadow: none !important;
        background-color: transparent !important;
        /* iti adds an internal padding-left to make space for an in-input
           country selector. Our country selector is in a separate sibling
           button (separateDialCode), so the placeholder gets pushed to the
           middle. Reset to the form's standard horizontal padding so it
           lines up with the other address rows. */
        padding-left: var(--cp_checkout-form-fields-input-padding-x) !important;
        padding-right: var(--cp_checkout-form-fields-input-padding-x) !important;
      }
      /* Focus: restore the themed ring/background that the reset above strips.
         Same unlayered tier as the reset, later in source and more specific, so
         it wins the !important tie — while seller customCss (injected later)
         can still out-!important it. */
      .cp-address-inputs-group .cp-address-phone-row .iti__tel-input:focus,
      .cp-address-inputs-group .cp-address-phone-row .cp_form-telinput:focus {
        background-color: var(--cp_checkout-form-fields-input-focus-background-color) !important;
        box-shadow:
          0 0 0 var(--cp_checkout-form-fields-input-border-width) var(--cp_checkout-form-fields-input-focus-border-color),
          0 0 0 calc(var(--cp_checkout-form-fields-input-border-width) + 3px) var(--cp_checkout-form-fields-input-focus-ring-color) !important;
      }
      /* Autofilled variant: the UA overrides the background-color, so the
         focus background rides in as an inset cover, same tier and
         !important as the rule above so it wins the tie later in source. */
      .cp-address-inputs-group .cp-address-phone-row .iti__tel-input:-webkit-autofill:focus,
      .cp-address-inputs-group .cp-address-phone-row .cp_form-telinput:-webkit-autofill:focus {
        box-shadow:
          inset 0 0 0 1000px var(--cp_checkout-form-fields-input-focus-background-color),
          0 0 0 var(--cp_checkout-form-fields-input-border-width) var(--cp_checkout-form-fields-input-focus-border-color),
          0 0 0 calc(var(--cp_checkout-form-fields-input-border-width) + 3px) var(--cp_checkout-form-fields-input-focus-ring-color) !important;
      }
      .cp-address-phone-row .iti__country-container {
        position: relative;
      }
      .cp-address-phone-row .iti__country-container > .iti__selected-country {
        border: 0 !important;
        border-right: 1px solid var(--cp_checkout-form-fields-input-border-color) !important;
        border-radius: 0 !important;
        background-color: transparent !important;
        padding-left: var(--cp_checkout-form-fields-input-padding-x) !important;
        padding-right: var(--cp_checkout-form-fields-input-padding-x) !important;
        gap: 8px;
      }
      .cp-address-phone-row .iti__country-container > .iti__selected-country:focus,
      .cp-address-phone-row .iti__country-container > .iti__selected-country:focus-visible {
        background-color: var(--cp_checkout-form-fields-input-focus-background-color) !important;
      }
.cp-address-phone-row .iti__country-container > .iti__selected-country .iti__selected-country-primary {
        background-color: transparent !important;
        border: 0 !important;
        padding: 0 !important;
        gap: 6px;
      }
      .cp-address-phone-row .iti__country-container > .iti__selected-country .iti__selected-dial-code {
        margin-left: 4px;
        color: var(--cp_checkout-form-fields-input-color);
      }
      /* Country dropdown: inherit the form's input theme so it works in
         dark mode, and match the form's font size instead of iti's
         default 16px. */
      .cp-address-phone-row .iti__dropdown-content {
        background-color: var(--cp_checkout-form-fields-input-background-color) !important;
        color: var(--cp_checkout-form-fields-input-color) !important;
        border: 1px solid var(--cp_checkout-form-fields-input-border-color) !important;
        border-radius: var(--cp_checkout-form-fields-input-border-radius) !important;
        font-family: var(--cp_checkout-form-fields-input-font-family);
        font-size: var(--cp_checkout-form-fields-input-font-size);
      }
      .cp-address-phone-row .iti__country-list,
      .cp-address-phone-row .iti__country,
      .cp-address-phone-row .iti__country-name,
      .cp-address-phone-row .iti__dial-code {
        color: var(--cp_checkout-form-fields-input-color) !important;
        font-size: var(--cp_checkout-form-fields-input-font-size);
      }
      .cp-address-phone-row .iti__country.iti__highlight,
      .cp-address-phone-row .iti__country:hover {
        background-color: color-mix(in oklab, var(--cp_checkout-form-fields-input-color) 8%, transparent) !important;
      }
      .cp-address-phone-row .iti__search-input {
        background-color: var(--cp_checkout-form-fields-input-background-color) !important;
        color: var(--cp_checkout-form-fields-input-color) !important;
        border-color: var(--cp_checkout-form-fields-input-border-color) !important;
        font-size: max(var(--cp_input-min-font-size, 0px), var(--cp_checkout-form-fields-input-font-size)) !important;
      }
    `;

export function getThemeCss({
  theme,
  seller,
  isPreview = false,
  previewColorMode = null,
  checkoutLayout,
  product = null,
}) {
  const tokens = theme.tokens;
  const elements = theme.elements || {};

  // Override color scheme with preview color mode if in preview
  // When in preview mode, previewColorMode should always override, even for system mode
  const colorScheme = tokens.colors;

  const isLightMode = colorScheme === 'light';
  const isDarkMode = colorScheme === 'dark';
  const isSystemMode = colorScheme === 'system';

  const colorPrimaryLight = tokens.colorPrimary?.light || seller.brandColor;
  const colorPrimaryDark = tokens.colorPrimary?.dark || seller.brandColor || colorPrimaryLight;

  const colorSecondaryLight = tokens.colorSecondary?.light || colorPrimaryLight;
  const colorSecondaryDark = tokens.colorSecondary?.dark || colorPrimaryDark;

  const layout = theme.layout;

  const layoutVariablesLight = getLayoutVariables(layout, elements, false, checkoutLayout, tokens);
  const layoutVariablesDark = getLayoutVariables(layout, elements, true, checkoutLayout, tokens);

  const colorTokensLight = getTokenColorVariables(
    tokens,
    false,
    colorPrimaryLight,
    colorSecondaryLight,
  );
  const colorTokensDark = getTokenColorVariables(
    tokens,
    true,
    colorPrimaryDark,
    colorSecondaryDark,
  );

  const elementStylesLight = getElementStyleVariables(
    elements,
    true,
    product,
    colorPrimaryLight,
    colorSecondaryLight,
  );
  const elementStylesDark = getElementStyleVariables(
    elements,
    false,
    product,
    colorPrimaryDark,
    colorSecondaryDark,
  );

  const styleTokens = getStyleTokens(tokens);

  const generateCssString = () => {
    // Preview-only: smooth transitions when customization settings change
    const previewTransitions = `
      .preview-container,
      .preview-container *:not(.no-preview-transition) {
        transition-property: color, background-color, border-color, box-shadow,
                             border-radius, font-size, font-weight, padding, border-width, opacity;
        transition-duration: 0.2s;
        transition-timing-function: ease;
      }
      .no-color-mode-transition,
      .no-color-mode-transition * {
        transition: none !important;
      }
    `;

    if (previewColorMode) {
      const effectivePreviewDark = getEffectivePreviewDark(colorScheme, previewColorMode);

      if (effectivePreviewDark) {
        return `
          :root {
            ${[
              ...styleTokens,
              ...layoutVariablesDark,
              ...colorTokensDark,
              ...elementStylesDark,
            ].join('\n')}
          }
          ${DEFAULT_INPUT_STYLES}
          ${previewTransitions}
        `;
      } else {
        return `
          :root {
            ${[
              ...styleTokens,
              ...layoutVariablesLight,
              ...colorTokensLight,
              ...elementStylesLight,
            ].join('\n')}
          }
          ${DEFAULT_INPUT_STYLES}
          ${previewTransitions}
        `;
      }
    }

    if (isLightMode) {
      return `
        :root {
          ${[
            ...styleTokens,
            ...layoutVariablesLight,
            ...colorTokensLight,
            ...elementStylesLight,
          ].join('\n')}
        }
        ${DEFAULT_INPUT_STYLES}
      `;
    } else if (isDarkMode) {
      return `
        :root {
          ${[...styleTokens, ...layoutVariablesDark, ...colorTokensDark, ...elementStylesDark].join(
            '\n',
          )}
        }
        ${DEFAULT_INPUT_STYLES}
      `;
    } else if (isSystemMode) {
      // Default to light mode, apply dark mode if system prefers it
      return `
        :root {
          ${[
            ...styleTokens,
            ...layoutVariablesLight,
            ...colorTokensLight,
            ...elementStylesLight,
          ].join('\n')}
        }

        @media (prefers-color-scheme: dark) {
          :root {
            ${[
              ...styleTokens,
              ...layoutVariablesDark,
              ...colorTokensDark,
              ...elementStylesDark,
            ].join('\n')}
          }
        }
        ${DEFAULT_INPUT_STYLES}
        ${isPreview ? previewTransitions : ''}
      `;
    }
    // Default case: no color mode explicitly set, default to light
    return `
      :root {
        ${[
          ...styleTokens,
          ...layoutVariablesLight,
          ...colorTokensLight,
          ...elementStylesLight,
        ].join('\n')}
      }
      ${DEFAULT_INPUT_STYLES}
    `;
  };

  return generateCssString();
}

export default function CheckoutCSS() {
  const {
    theme,
    seller,
    isTesting,
    isPreview,
    isTestingPayments,
    previewColorMode,
    checkout,
    checkoutLayout,
    isMeasurer,
  } = useCheckoutState();

  const shouldShowCustomCSS = (() => {
    // measuring in dashboard
    if (!isPreview && isTesting && isTestingPayments) return false;

    // testing payments in dashboard
    if (isTestingPayments) return true;

    // preview in dashboard
    if (!isPreview) return true;

    // livemode
    if (!isTesting) return true;

    return false;
  })();

  const tokens = theme.tokens;

  // Suppress transitions briefly when color mode changes to avoid flashing.
  // useLayoutEffect runs after DOM update but before paint, so the class is
  // present when the browser first renders the new colors.
  const effectiveColorMode = `${tokens.colors}-${previewColorMode}`;
  React.useLayoutEffect(() => {
    const container = document.querySelector('.preview-container');
    if (!container) return;
    container.classList.add('no-color-mode-transition');
    requestAnimationFrame(() => {
      container.classList.remove('no-color-mode-transition');
    });
  }, [effectiveColorMode]);

  const themeCss = React.useMemo(
    () =>
      getThemeCss({
        theme,
        seller,
        isPreview,
        previewColorMode,
        checkoutLayout,
        product: checkout?.product,
      }),
    [theme, seller, isPreview, previewColorMode, checkoutLayout, checkout?.product],
  );

  // The embed measurer shares the same document as the preview but uses
  // CENTER layout, so its :root variables (e.g. --cp_content-background-color)
  // would overwrite the preview's layout-specific values.  Scope the
  // measurer's CSS to its container so it doesn't leak.
  const cssString = isMeasurer
    ? themeCss.replaceAll(':root', `#checkout-embed-measurer-${checkout._id}`)
    : themeCss;

  return (
    <>
      <style
        dangerouslySetInnerHTML={{
          __html: cssString,
        }}
      />

      {shouldShowCustomCSS && <style>{theme?.customCss}</style>}
    </>
  );
}
